import {
  marketingTheme
} from "./chunk-OPHPJ2WG.mjs";
import {
  Icon
} from "./chunk-HGGC2VGG.mjs";
import {
  brand,
  marketing,
  marketingBreakpoints,
  maxWidths,
  radii,
  sectionSpacing
} from "./chunk-RWJTCKM5.mjs";

// src/marketing/theme.ts
import React, { createContext, useContext } from "react";
var MarketingThemeContext = createContext(marketingTheme);
var MarketingThemeProvider = ({
  children,
  theme = marketingTheme
}) => {
  return React.createElement(
    MarketingThemeContext.Provider,
    { value: theme },
    children
  );
};
var useMarketingTheme = () => {
  return useContext(MarketingThemeContext);
};

// src/marketing/components/Container.tsx
import { View } from "react-native";
import { jsx } from "react/jsx-runtime";
var Container = ({
  maxWidth: mw = maxWidths.marketing,
  paddingX = 80,
  style,
  children,
  ...rest
}) => /* @__PURE__ */ jsx(
  View,
  {
    style: [
      {
        width: "100%",
        maxWidth: mw,
        alignSelf: "center",
        paddingHorizontal: paddingX
      },
      style
    ],
    ...rest,
    children
  }
);
Container.displayName = "Container";

// src/marketing/components/Section.tsx
import { View as View2 } from "react-native";
import { jsx as jsx2 } from "react/jsx-runtime";
var Section = ({
  bg,
  paddingY = sectionSpacing.section,
  gap = sectionSpacing.sectionGap,
  center = true,
  paddingX = 112,
  style,
  children,
  ...rest
}) => /* @__PURE__ */ jsx2(
  View2,
  {
    style: [
      {
        paddingVertical: paddingY,
        paddingHorizontal: paddingX,
        gap,
        ...bg && { backgroundColor: bg },
        ...center && { alignItems: "center" }
      },
      style
    ],
    nativeID: rest.id,
    ...rest,
    children
  }
);
Section.displayName = "Section";

// src/marketing/components/Button.tsx
import { Pressable, Platform } from "react-native";

// src/primitives/Text.tsx
import React2 from "react";
import { Text as RNText } from "react-native";
import { jsx as jsx3 } from "react/jsx-runtime";
var Text = React2.forwardRef(
  ({
    size,
    color,
    fontFamily,
    weight,
    align,
    lineHeight,
    letterSpacing,
    transform,
    decoration,
    opacity,
    style,
    ...rest
  }, ref) => {
    const textStyle = {
      ...size !== void 0 && { fontSize: size },
      ...color && { color },
      ...fontFamily && { fontFamily },
      ...weight && { fontWeight: weight },
      ...align && { textAlign: align },
      ...lineHeight !== void 0 && { lineHeight },
      ...letterSpacing !== void 0 && { letterSpacing },
      ...transform && { textTransform: transform },
      ...decoration && { textDecorationLine: decoration },
      ...opacity !== void 0 && { opacity }
    };
    return /* @__PURE__ */ jsx3(RNText, { ref, style: [textStyle, style], ...rest });
  }
);
Text.displayName = "Text";

// src/marketing/components/Button.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var transition = Platform.select({
  web: { transitionProperty: "background-color, border-color, box-shadow, opacity", transitionDuration: "200ms", transitionTimingFunction: "ease" },
  default: {}
});
var variantStyles = {
  primary: {
    container: {
      backgroundColor: brand.purple,
      paddingHorizontal: 36,
      paddingVertical: 16,
      borderRadius: radii.full,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      ...transition
    },
    text: {
      color: brand.black,
      fontFamily: "Plus Jakarta Sans",
      fontWeight: "600",
      fontSize: 16,
      textTransform: "uppercase"
    },
    hover: {
      backgroundColor: brand.purpleHover
    }
  },
  secondary: {
    container: {
      borderWidth: 1,
      borderColor: marketing.surface,
      paddingHorizontal: 36,
      paddingVertical: 16,
      borderRadius: radii.full,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      ...transition
    },
    text: {
      color: marketing.textBody,
      fontFamily: "Plus Jakarta Sans",
      fontWeight: "500",
      fontSize: 16,
      textTransform: "uppercase"
    },
    hover: {
      borderColor: marketing.textMuted,
      backgroundColor: marketing.surfaceHover
    }
  },
  outline: {
    container: {
      borderWidth: 1,
      borderColor: brand.purple,
      paddingHorizontal: 36,
      paddingVertical: 16,
      borderRadius: radii.full,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      ...transition
    },
    text: {
      color: brand.purple,
      fontFamily: "Plus Jakarta Sans",
      fontWeight: "500",
      fontSize: 17,
      textTransform: "uppercase"
    },
    hover: {
      borderColor: brand.purpleHover,
      backgroundColor: `${brand.purple}14`
    }
  },
  small: {
    container: {
      backgroundColor: brand.purple,
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: radii.full,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      ...transition
    },
    text: {
      color: brand.black,
      fontFamily: "Plus Jakarta Sans",
      fontWeight: "600",
      fontSize: 14,
      textTransform: "uppercase"
    },
    hover: {
      backgroundColor: brand.purpleHover
    }
  }
};
var Button = ({
  label,
  variant = "primary",
  onPress,
  icon,
  disabled
}) => {
  const styles = variantStyles[variant];
  return /* @__PURE__ */ jsxs(
    Pressable,
    {
      onPress,
      disabled,
      style: ({ pressed, hovered }) => [
        styles.container,
        hovered && styles.hover,
        pressed && { opacity: 0.9 },
        disabled && { opacity: 0.5 }
      ],
      children: [
        /* @__PURE__ */ jsx4(Text, { style: styles.text, children: label }),
        icon
      ]
    }
  );
};
Button.displayName = "Button";

// src/marketing/components/Card.tsx
import { View as View3 } from "react-native";
import { jsx as jsx5 } from "react/jsx-runtime";
var Card = ({
  bg = marketing.card,
  borderColor = marketing.border,
  radius = radii.xl,
  padding = 32,
  gap = 20,
  style,
  children,
  ...rest
}) => /* @__PURE__ */ jsx5(
  View3,
  {
    style: [
      {
        backgroundColor: bg,
        borderWidth: 1,
        borderColor,
        borderRadius: radius,
        padding,
        gap,
        flexDirection: "column"
      },
      style
    ],
    ...rest,
    children
  }
);
Card.displayName = "Card";

// src/marketing/components/SectionBadge.tsx
import { View as View4 } from "react-native";
import { jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var SectionBadge = ({
  label,
  color = brand.purple,
  tint = marketing.purpleTint,
  icon
}) => /* @__PURE__ */ jsxs2(
  View4,
  {
    style: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      backgroundColor: tint,
      borderRadius: radii.full,
      paddingHorizontal: 16,
      paddingVertical: 6
    },
    children: [
      icon,
      /* @__PURE__ */ jsx6(
        Text,
        {
          size: 12,
          weight: "600",
          color,
          letterSpacing: 0.5,
          fontFamily: "Plus Jakarta Sans",
          children: label
        }
      )
    ]
  }
);
SectionBadge.displayName = "SectionBadge";

// src/marketing/components/GradientText.tsx
import { Text as RNText2, Platform as Platform2 } from "react-native";
import { jsx as jsx7 } from "react/jsx-runtime";
var GradientText = ({
  size = 72,
  fontFamily = "Butler",
  weight = "300",
  lineHeight,
  letterSpacing = -1,
  from = "#14B881",
  to = "#6EA6F8",
  style,
  children,
  ...rest
}) => {
  const computedLineHeight = lineHeight ? size * lineHeight : size * 1.1;
  const baseStyle = {
    fontSize: size,
    fontFamily,
    fontWeight: weight,
    lineHeight: computedLineHeight,
    letterSpacing
  };
  if (Platform2.OS === "web") {
    return /* @__PURE__ */ jsx7(
      RNText2,
      {
        style: [
          baseStyle,
          {
            // @ts-expect-error — web-only CSS properties via react-native-web
            backgroundImage: `linear-gradient(90deg, ${from}, ${to})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text"
          },
          style
        ],
        ...rest,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx7(
    RNText2,
    {
      style: [baseStyle, { color: from }, style],
      ...rest,
      children
    }
  );
};
GradientText.displayName = "GradientText";

// src/marketing/components/ChecklistItem.tsx
import { View as View5 } from "react-native";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var ChecklistItem = ({
  label,
  color = brand.purple,
  icon
}) => /* @__PURE__ */ jsxs3(View5, { style: { flexDirection: "row", alignItems: "center", gap: 10 }, children: [
  icon ?? /* @__PURE__ */ jsx8(
    View5,
    {
      style: {
        width: 16,
        height: 16,
        justifyContent: "center",
        alignItems: "center"
      },
      children: /* @__PURE__ */ jsx8(Text, { size: 14, color, weight: "700", children: "\u2713" })
    }
  ),
  /* @__PURE__ */ jsx8(
    Text,
    {
      size: 13,
      color: marketing.textBody,
      fontFamily: "Plus Jakarta Sans",
      children: label
    }
  )
] });
ChecklistItem.displayName = "ChecklistItem";

// src/marketing/components/Navbar.tsx
import { useState, useCallback } from "react";
import { View as View6, Platform as Platform3 } from "react-native";

// src/primitives/Pressable.tsx
import React3 from "react";
import {
  Pressable as RNPressable
} from "react-native";
import { jsx as jsx9 } from "react/jsx-runtime";
var Pressable2 = React3.forwardRef(
  ({
    bg,
    borderRadius,
    padding,
    paddingX,
    paddingY,
    align,
    justify,
    direction,
    gap,
    flex,
    width,
    height,
    borderWidth,
    borderColor,
    style,
    ...rest
  }, ref) => {
    const baseStyle = {
      ...bg && { backgroundColor: bg },
      ...borderRadius !== void 0 && { borderRadius },
      ...padding !== void 0 && { padding },
      ...paddingX !== void 0 && { paddingHorizontal: paddingX },
      ...paddingY !== void 0 && { paddingVertical: paddingY },
      ...align && { alignItems: align },
      ...justify && { justifyContent: justify },
      ...direction && { flexDirection: direction },
      ...gap !== void 0 && { gap },
      ...flex !== void 0 && { flex },
      ...width !== void 0 && { width },
      ...height !== void 0 && { height },
      ...borderWidth !== void 0 && { borderWidth },
      ...borderColor && { borderColor }
    };
    return /* @__PURE__ */ jsx9(
      RNPressable,
      {
        ref,
        style: (state) => [
          baseStyle,
          typeof style === "function" ? style(state) : style
        ],
        ...rest
      }
    );
  }
);
Pressable2.displayName = "Pressable";

// src/marketing/components/Navbar.tsx
import { Fragment, jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
var transition2 = Platform3.select({
  web: {
    transitionProperty: "background-color, border-color, color, opacity",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease"
  },
  default: {}
});
var NavDropdown = ({ items, visible }) => /* @__PURE__ */ jsx10(
  "div",
  {
    style: {
      position: "absolute",
      top: "100%",
      left: 0,
      paddingTop: 12,
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? "auto" : "none",
      transition: "opacity 200ms ease",
      zIndex: 200
    },
    children: /* @__PURE__ */ jsx10(
      "div",
      {
        style: {
          background: marketing.card,
          border: `1px solid ${marketing.border}`,
          borderRadius: radii.md,
          padding: "8px 0",
          minWidth: 280,
          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch"
        },
        children: items.map((item) => /* @__PURE__ */ jsx10(NavDropdownItem, { item }, item.label))
      }
    )
  }
);
var NavDropdownItem = ({ item }) => /* @__PURE__ */ jsx10(
  Pressable2,
  {
    onPress: item.onPress,
    style: ({ hovered }) => [
      {
        paddingHorizontal: 24,
        paddingVertical: 12,
        whiteSpace: "nowrap",
        ...transition2
      },
      hovered && { backgroundColor: marketing.cardHighlight }
    ],
    children: ({ hovered }) => /* @__PURE__ */ jsx10(
      Text,
      {
        size: 14,
        color: hovered ? marketing.textHeading : marketing.textBody,
        fontFamily: "Plus Jakarta Sans",
        weight: "500",
        align: "left",
        style: { whiteSpace: "nowrap" },
        children: item.label
      }
    )
  }
);
var NavItem = ({ link }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = link.children && link.children.length > 0;
  const handleEnter = useCallback(() => {
    if (hasChildren) setOpen(true);
  }, [hasChildren]);
  const handleLeave = useCallback(() => {
    if (hasChildren) setOpen(false);
  }, [hasChildren]);
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      style: { position: "relative" },
      onMouseEnter: handleEnter,
      onMouseLeave: handleLeave,
      children: [
        /* @__PURE__ */ jsx10(
          Pressable2,
          {
            onPress: link.onPress,
            style: ({ hovered }) => [
              {
                ...transition2,
                flexDirection: "row",
                alignItems: "center",
                gap: 6
              },
              hovered && { opacity: 1 }
            ],
            children: ({ hovered }) => /* @__PURE__ */ jsxs4(Fragment, { children: [
              /* @__PURE__ */ jsx10(
                Text,
                {
                  size: 14,
                  color: hovered || open || link.isActive ? marketing.textHeading : marketing.textBody,
                  fontFamily: "Plus Jakarta Sans",
                  weight: link.isActive ? "600" : "500",
                  children: link.label
                }
              ),
              hasChildren && /* @__PURE__ */ jsx10(
                Text,
                {
                  size: 10,
                  color: hovered || open || link.isActive ? marketing.textHeading : marketing.textBody,
                  fontFamily: "Plus Jakarta Sans",
                  children: "\u25BE"
                }
              )
            ] })
          }
        ),
        hasChildren && /* @__PURE__ */ jsx10(NavDropdown, { items: link.children, visible: open })
      ]
    }
  );
};
var NavButtonSecondary = ({
  label,
  onPress
}) => /* @__PURE__ */ jsx10(
  Pressable2,
  {
    onPress,
    style: ({ hovered }) => [
      {
        borderWidth: 1,
        borderColor: brand.purple,
        backgroundColor: marketing.surface,
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: radii.sm,
        ...transition2
      },
      hovered && {
        borderColor: brand.purpleHover,
        backgroundColor: marketing.cardHighlight
      }
    ],
    children: /* @__PURE__ */ jsx10(
      Text,
      {
        size: 14,
        color: brand.purple,
        fontFamily: "Plus Jakarta Sans",
        weight: "600",
        transform: "uppercase",
        children: label
      }
    )
  }
);
var NavButtonPrimary = ({
  label,
  onPress
}) => /* @__PURE__ */ jsx10(
  Pressable2,
  {
    onPress,
    style: ({ hovered }) => [
      {
        backgroundColor: brand.purple,
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: radii.sm,
        ...transition2
      },
      hovered && {
        backgroundColor: brand.purpleHover
      }
    ],
    children: /* @__PURE__ */ jsx10(
      Text,
      {
        size: 14,
        color: brand.black,
        fontFamily: "Plus Jakarta Sans",
        weight: "600",
        transform: "uppercase",
        children: label
      }
    )
  }
);
var UserAvatar = ({ onPress }) => /* @__PURE__ */ jsx10(
  Pressable2,
  {
    onPress,
    style: ({ hovered }) => [
      {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: marketing.cardHighlight,
        borderWidth: 1,
        borderColor: marketing.border,
        alignItems: "center",
        justifyContent: "center",
        ...transition2
      },
      hovered && {
        borderColor: brand.purple,
        backgroundColor: marketing.card
      }
    ],
    children: /* @__PURE__ */ jsx10("svg", { width: "18", height: "18", viewBox: "0 0 256 256", fill: marketing.textBody, children: /* @__PURE__ */ jsx10("path", { d: "M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z" }) })
  }
);
var Navbar = ({
  logo,
  links = [],
  cta,
  loginLink,
  userMenu,
  extras
}) => /* @__PURE__ */ jsxs4(
  View6,
  {
    style: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 24,
      paddingHorizontal: 80,
      paddingVertical: 24,
      width: "100%",
      zIndex: 100
    },
    children: [
      /* @__PURE__ */ jsxs4(
        View6,
        {
          style: {
            flexDirection: "row",
            alignItems: "center",
            gap: 40,
            flexShrink: 1,
            minWidth: 0
          },
          children: [
            logo,
            links.length > 0 && /* @__PURE__ */ jsx10(View6, { style: { flexDirection: "row", alignItems: "center", gap: 24, flexShrink: 1 }, children: links.map((link) => /* @__PURE__ */ jsx10(NavItem, { link }, link.label)) })
          ]
        }
      ),
      /* @__PURE__ */ jsxs4(
        View6,
        {
          style: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            flexShrink: 0
          },
          children: [
            extras,
            userMenu ? /* @__PURE__ */ jsxs4(Fragment, { children: [
              /* @__PURE__ */ jsx10(NavButtonPrimary, { label: "APP", onPress: userMenu.onAppPress }),
              /* @__PURE__ */ jsx10(UserAvatar, { onPress: userMenu.onAvatarPress })
            ] }) : /* @__PURE__ */ jsxs4(Fragment, { children: [
              loginLink && /* @__PURE__ */ jsx10(NavButtonSecondary, { label: loginLink.label, onPress: loginLink.onPress }),
              cta && /* @__PURE__ */ jsx10(NavButtonPrimary, { label: cta.label, onPress: cta.onPress })
            ] })
          ]
        }
      )
    ]
  }
);
Navbar.displayName = "Navbar";

// src/marketing/components/Hero.tsx
import { View as View7 } from "react-native";
import { jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
var Hero = ({
  headlineSans,
  headlineSerif,
  description,
  badge,
  primaryCta,
  secondaryCta,
  footer
}) => /* @__PURE__ */ jsxs5(
  View7,
  {
    style: {
      alignItems: "center",
      paddingHorizontal: 80,
      paddingTop: 96,
      paddingBottom: 80,
      gap: 32
    },
    children: [
      badge && /* @__PURE__ */ jsx11(SectionBadge, { label: badge.label, icon: badge.icon }),
      /* @__PURE__ */ jsx11(
        Text,
        {
          size: 72,
          weight: "600",
          color: marketing.textHeading,
          fontFamily: "Plus Jakarta Sans",
          letterSpacing: -1,
          lineHeight: 72 * 1.1,
          align: "center",
          children: headlineSans
        }
      ),
      /* @__PURE__ */ jsx11(
        GradientText,
        {
          size: 72,
          fontFamily: "Butler",
          weight: "300",
          letterSpacing: -1,
          lineHeight: 1.1,
          style: { textAlign: "center" },
          children: headlineSerif
        }
      ),
      /* @__PURE__ */ jsx11(
        Text,
        {
          size: 18,
          color: marketing.textMuted,
          fontFamily: "Plus Jakarta Sans",
          lineHeight: 18 * 1.6,
          align: "center",
          style: { maxWidth: 576 },
          children: description
        }
      ),
      (primaryCta || secondaryCta) && /* @__PURE__ */ jsxs5(View7, { style: { flexDirection: "row", alignItems: "center", gap: 16, marginTop: 8 }, children: [
        primaryCta && /* @__PURE__ */ jsx11(
          Button,
          {
            label: primaryCta.label,
            variant: "primary",
            onPress: primaryCta.onPress,
            icon: primaryCta.icon
          }
        ),
        secondaryCta && /* @__PURE__ */ jsx11(
          Button,
          {
            label: secondaryCta.label,
            variant: "secondary",
            onPress: secondaryCta.onPress
          }
        )
      ] }),
      footer
    ]
  }
);
Hero.displayName = "Hero";

// src/marketing/components/Footer.tsx
import { View as View9, Platform as Platform4 } from "react-native";

// src/primitives/Divider.tsx
import { View as View8 } from "react-native";
import { jsx as jsx12 } from "react/jsx-runtime";
var Divider = ({
  color = marketing.border,
  thickness = 1,
  vertical
}) => /* @__PURE__ */ jsx12(
  View8,
  {
    style: vertical ? { width: thickness, backgroundColor: color, alignSelf: "stretch" } : { height: thickness, backgroundColor: color, width: "100%" }
  }
);
Divider.displayName = "Divider";

// src/marketing/components/Footer.tsx
import { jsx as jsx13, jsxs as jsxs6 } from "react/jsx-runtime";
var transition3 = Platform4.select({
  web: { transitionProperty: "opacity", transitionDuration: "200ms", transitionTimingFunction: "ease" },
  default: {}
});
var Footer = ({
  logo,
  tagline,
  afterTagline,
  columns,
  copyright,
  legalLinks,
  socialLinks
}) => /* @__PURE__ */ jsxs6(
  View9,
  {
    style: {
      backgroundColor: marketing.canvas,
      paddingHorizontal: 112,
      paddingTop: 64,
      paddingBottom: 48,
      gap: 48
    },
    children: [
      /* @__PURE__ */ jsxs6(View9, { style: { flexDirection: "row", justifyContent: "space-between" }, children: [
        /* @__PURE__ */ jsxs6(View9, { style: { gap: 16, maxWidth: 280 }, children: [
          logo,
          tagline && /* @__PURE__ */ jsx13(
            Text,
            {
              size: 14,
              color: marketing.textSubtle,
              fontFamily: "Plus Jakarta Sans",
              lineHeight: 14 * 1.6,
              children: tagline
            }
          ),
          afterTagline
        ] }),
        /* @__PURE__ */ jsx13(View9, { style: { flexDirection: "row", gap: 80 }, children: columns.map((col) => /* @__PURE__ */ jsxs6(View9, { style: { gap: 16 }, children: [
          /* @__PURE__ */ jsx13(
            Text,
            {
              size: 13,
              color: marketing.textBody,
              fontFamily: "Plus Jakarta Sans",
              weight: "600",
              children: col.title
            }
          ),
          col.links.map((link) => (
            // `href` reaches react-native-web through Pressable's rest props
            // and makes it render a real `<a>`. `onPress` still runs, so the
            // app router handles the click and the browser never reloads.
            /* @__PURE__ */ jsx13(
              Pressable2,
              {
                href: link.href,
                onPress: link.onPress,
                style: ({ hovered }) => [
                  { ...transition3 },
                  hovered && { opacity: 1 }
                ],
                children: ({ hovered }) => /* @__PURE__ */ jsx13(
                  Text,
                  {
                    size: 13,
                    color: hovered ? marketing.textBody : marketing.textMuted,
                    fontFamily: "Plus Jakarta Sans",
                    children: link.label
                  }
                )
              },
              link.label
            )
          ))
        ] }, col.title)) })
      ] }),
      /* @__PURE__ */ jsx13(Divider, {}),
      /* @__PURE__ */ jsxs6(View9, { style: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" }, children: [
        /* @__PURE__ */ jsxs6(View9, { style: { flexDirection: "row", alignItems: "center", gap: 16 }, children: [
          copyright && /* @__PURE__ */ jsx13(Text, { size: 12, color: marketing.textSubtle, fontFamily: "Plus Jakarta Sans", children: copyright }),
          legalLinks && legalLinks.map((link) => (
            // Terms and Privacy are internal pages. They were opened with
            // `window.open(href, '_blank')`, which both stripped their link
            // semantics and forced a new tab for a same-site route. A plain
            // `href` restores normal link behaviour.
            /* @__PURE__ */ jsx13(
              Pressable2,
              {
                href: link.href,
                style: ({ hovered }) => [
                  { ...transition3 },
                  hovered && { opacity: 1 }
                ],
                children: ({ hovered }) => /* @__PURE__ */ jsx13(
                  Text,
                  {
                    size: 12,
                    color: hovered ? marketing.textBody : marketing.textSubtle,
                    fontFamily: "Plus Jakarta Sans",
                    children: link.label
                  }
                )
              },
              link.label
            )
          ))
        ] }),
        socialLinks && /* @__PURE__ */ jsx13(View9, { style: { flexDirection: "row", gap: 20 }, children: socialLinks.map((link) => (
          // Social links DO belong in a new tab, but that is `target`'s job,
          // not `window.open`'s. Keeping the href means they are real links
          // and `rel` can protect the opener.
          /* @__PURE__ */ jsx13(
            Pressable2,
            {
              href: link.href,
              ...link.href ? { hrefAttrs: { target: "_blank", rel: "noopener noreferrer" } } : {},
              onPress: link.href ? void 0 : link.onPress,
              style: ({ hovered }) => [
                { ...transition3 },
                hovered && { opacity: 0.7 }
              ],
              children: link.icon ?? /* @__PURE__ */ jsx13(Text, { size: 12, color: marketing.textMuted, fontFamily: "Plus Jakarta Sans", children: link.label })
            },
            link.label
          )
        )) })
      ] })
    ]
  }
);
Footer.displayName = "Footer";

// src/marketing/components/FeatureCard.tsx
import { View as View10, Platform as Platform6 } from "react-native";

// src/marketing/components/DotGrid.tsx
import React5, { useEffect } from "react";
import { Platform as Platform5 } from "react-native";
var KEYFRAMES_ID = "ds-dot-grid-keyframes";
function ensureKeyframes() {
  const doc = typeof globalThis !== "undefined" ? globalThis.document : void 0;
  if (!doc) return;
  if (doc.getElementById(KEYFRAMES_ID)) return;
  const style = doc.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes dsDotGridDrift {
      0%   { background-position: 0px 0px; }
      100% { background-position: 24px 24px; }
    }
  `;
  doc.head.appendChild(style);
}
var DotGrid = ({
  color = "rgba(255,255,255,0.35)",
  dotSize = 1.5,
  spacing = 20,
  duration = 4
}) => {
  useEffect(() => {
    ensureKeyframes();
  }, []);
  if (Platform5.OS !== "web") return null;
  return React5.createElement("div", {
    style: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      pointerEvents: "none",
      backgroundImage: `radial-gradient(circle, ${color} ${dotSize}px, transparent ${dotSize}px)`,
      backgroundSize: `${spacing}px ${spacing}px`,
      maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 70%)",
      WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 70%)",
      animation: `dsDotGridDrift ${duration}s linear infinite`
    }
  });
};
DotGrid.displayName = "DotGrid";

// src/marketing/components/FeatureCard.tsx
import { jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
var transition4 = Platform6.select({
  web: { transitionProperty: "background-color, border-color, opacity", transitionDuration: "200ms", transitionTimingFunction: "ease" },
  default: {}
});
var FeatureCard = ({
  title,
  description,
  icon,
  iconTint = marketing.purpleTint,
  accentColor = brand.purple,
  previewLabel = "Preview",
  onPreviewPress,
  linkLabel = "Learn more",
  onLinkPress,
  hideDotGrid = false
}) => {
  const hasActions = onPreviewPress || onLinkPress;
  return /* @__PURE__ */ jsxs7(
    Card,
    {
      style: {
        flex: 1,
        position: "relative",
        overflow: "hidden"
      },
      children: [
        !hideDotGrid && /* @__PURE__ */ jsx14(DotGrid, { color: `${accentColor}55` }),
        /* @__PURE__ */ jsxs7(View10, { style: { zIndex: 1, gap: 20, flex: 1 }, children: [
          icon && /* @__PURE__ */ jsx14(
            View10,
            {
              style: {
                width: 48,
                height: 48,
                borderRadius: 12,
                backgroundColor: iconTint,
                justifyContent: "center",
                alignItems: "center"
              },
              children: icon
            }
          ),
          /* @__PURE__ */ jsx14(View10, { ...{ dataSet: { fcTitle: "" } }, style: { minHeight: 44 }, children: /* @__PURE__ */ jsx14(
            Text,
            {
              size: 18,
              weight: "600",
              color: marketing.textHeading,
              fontFamily: "Plus Jakarta Sans",
              children: title
            }
          ) }),
          /* @__PURE__ */ jsx14(View10, { style: { flex: 1 }, children: /* @__PURE__ */ jsx14(
            Text,
            {
              size: 14,
              color: marketing.textMuted,
              fontFamily: "Plus Jakarta Sans",
              lineHeight: 14 * 1.6,
              children: description
            }
          ) }),
          hasActions && /* @__PURE__ */ jsxs7(View10, { ...{ dataSet: { fcActions: "" } }, style: { flexDirection: "column", alignItems: "stretch", gap: 10, marginTop: "auto" }, children: [
            onPreviewPress && /* @__PURE__ */ jsx14(
              Pressable2,
              {
                onPress: onPreviewPress,
                style: ({ hovered }) => [
                  {
                    borderWidth: 1,
                    borderColor: accentColor,
                    borderRadius: radii.lg,
                    paddingHorizontal: 20,
                    paddingVertical: 8,
                    ...transition4
                  },
                  hovered && {
                    backgroundColor: `${accentColor}14`
                  }
                ],
                children: /* @__PURE__ */ jsx14(
                  Text,
                  {
                    size: 12,
                    weight: "600",
                    color: accentColor,
                    fontFamily: "Plus Jakarta Sans",
                    style: { textTransform: "uppercase", letterSpacing: 0.5 },
                    children: previewLabel
                  }
                )
              }
            ),
            onLinkPress && /* @__PURE__ */ jsx14(
              Pressable2,
              {
                onPress: onLinkPress,
                style: ({ hovered }) => [
                  { ...transition4 },
                  hovered && { opacity: 0.8 }
                ],
                children: ({ hovered }) => /* @__PURE__ */ jsx14(
                  Text,
                  {
                    size: 12,
                    weight: "600",
                    color: hovered ? marketing.textBody : marketing.textMuted,
                    fontFamily: "Plus Jakarta Sans",
                    style: { textTransform: "uppercase", letterSpacing: 0.5 },
                    children: linkLabel
                  }
                )
              }
            )
          ] })
        ] })
      ]
    }
  );
};
FeatureCard.displayName = "FeatureCard";

// src/marketing/components/MetricCard.tsx
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
var MetricCard = ({
  value,
  label,
  color = brand.purple
}) => /* @__PURE__ */ jsxs8(
  Card,
  {
    gap: 12,
    style: {
      flex: 1,
      alignItems: "center"
    },
    children: [
      /* @__PURE__ */ jsx15(
        Text,
        {
          size: 36,
          weight: "500",
          color,
          fontFamily: "Plus Jakarta Sans",
          letterSpacing: -0.5,
          children: value
        }
      ),
      /* @__PURE__ */ jsx15(
        Text,
        {
          size: 14,
          weight: "500",
          color: marketing.textMuted,
          fontFamily: "Plus Jakarta Sans",
          children: label
        }
      )
    ]
  }
);
MetricCard.displayName = "MetricCard";

// src/marketing/components/PricingCard.tsx
import { View as View11 } from "react-native";
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
var PricingCard = ({
  name,
  description,
  price,
  priceSuffix = "/month",
  priceLabel,
  pricePrefix,
  ctaLabel,
  onCtaPress,
  features,
  accentColor = brand.green,
  featureBars
}) => /* @__PURE__ */ jsxs9(Card, { gap: 24, style: { flex: 1 }, children: [
  /* @__PURE__ */ jsx16(
    Text,
    {
      size: 20,
      weight: "600",
      color: accentColor,
      fontFamily: "Plus Jakarta Sans",
      children: name
    }
  ),
  /* @__PURE__ */ jsx16(
    Text,
    {
      size: 14,
      color: marketing.textMuted,
      fontFamily: "Plus Jakarta Sans",
      lineHeight: 14 * 1.6,
      children: description
    }
  ),
  /* @__PURE__ */ jsxs9(View11, { children: [
    pricePrefix && /* @__PURE__ */ jsx16(
      Text,
      {
        size: 12,
        color: marketing.textMuted,
        fontFamily: "Plus Jakarta Sans",
        style: { marginBottom: 4 },
        children: pricePrefix
      }
    ),
    /* @__PURE__ */ jsxs9(View11, { style: { flexDirection: "row", alignItems: "baseline", gap: 4 }, children: [
      /* @__PURE__ */ jsx16(
        Text,
        {
          size: priceLabel && !price ? void 0 : 36,
          weight: "600",
          color: marketing.textHeading,
          fontFamily: "Plus Jakarta Sans",
          style: priceLabel && !price ? { fontSize: "clamp(24px, 7cqi, 36px)", whiteSpace: "nowrap" } : void 0,
          children: price ?? priceLabel ?? name
        }
      ),
      price && /* @__PURE__ */ jsx16(
        Text,
        {
          size: 14,
          color: marketing.textMuted,
          fontFamily: "Plus Jakarta Sans",
          children: priceSuffix
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx16(
    Button,
    {
      label: ctaLabel,
      variant: "small",
      onPress: onCtaPress
    }
  ),
  /* @__PURE__ */ jsx16(Divider, {}),
  features.map((feat, i) => {
    const bar = featureBars?.[i];
    return /* @__PURE__ */ jsxs9(View11, { children: [
      /* @__PURE__ */ jsx16(
        Text,
        {
          size: 15,
          weight: "600",
          color: marketing.textHeading,
          fontFamily: "Plus Jakarta Sans",
          children: feat.title
        }
      ),
      /* @__PURE__ */ jsx16(
        Text,
        {
          size: 12,
          color: marketing.textMuted,
          fontFamily: "Plus Jakarta Sans",
          style: { marginTop: 4 },
          children: feat.description
        }
      ),
      bar && /* @__PURE__ */ jsx16(
        "div",
        {
          style: {
            marginTop: 10,
            height: 2,
            width: `${bar.width}%`,
            background: bar.dashed ? `repeating-linear-gradient(90deg, ${bar.color} 0px, ${bar.color} 6px, transparent 6px, transparent 12px)` : `linear-gradient(90deg, ${bar.color}, ${bar.color}50)`,
            borderRadius: 1
          }
        }
      )
    ] }, feat.title);
  })
] });
PricingCard.displayName = "PricingCard";

// src/marketing/components/TrustedBy.tsx
import { View as View12 } from "react-native";
import { jsx as jsx17, jsxs as jsxs10 } from "react/jsx-runtime";
var TrustedBy = ({
  label = "Trusted by",
  names,
  logos
}) => /* @__PURE__ */ jsxs10(
  View12,
  {
    style: {
      flexDirection: "row",
      alignItems: "center",
      gap: 48,
      paddingTop: 40
    },
    children: [
      /* @__PURE__ */ jsx17(
        Text,
        {
          size: 12,
          color: marketing.textSubtle,
          fontFamily: "Plus Jakarta Sans",
          weight: "500",
          letterSpacing: 1,
          children: label
        }
      ),
      logos ? logos.map((logo, i) => /* @__PURE__ */ jsx17(View12, { children: logo }, i)) : names.map((name) => /* @__PURE__ */ jsx17(
        Text,
        {
          size: 14,
          color: marketing.textDim,
          fontFamily: "Plus Jakarta Sans",
          weight: "500",
          letterSpacing: 3,
          children: name
        },
        name
      ))
    ]
  }
);
TrustedBy.displayName = "TrustedBy";

// src/marketing/components/CardGrid.tsx
import React6 from "react";
import { View as View13 } from "react-native";
import { jsx as jsx18 } from "react/jsx-runtime";
var CardGrid = ({
  columns = 3,
  gap = sectionSpacing.cardGap,
  style,
  children,
  ...rest
}) => {
  const items = React6.Children.toArray(children);
  return /* @__PURE__ */ jsx18(
    View13,
    {
      style: [
        {
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "center",
          gap,
          width: "100%"
        },
        style
      ],
      ...rest,
      children: items.map((child, i) => /* @__PURE__ */ jsx18(
        View13,
        {
          style: {
            /* Equal column width accounting for gaps */
            width: `calc((100% - ${(columns - 1) * gap}px) / ${columns})`,
            alignSelf: "stretch"
          },
          children: child
        },
        child.key ?? i
      ))
    }
  );
};
CardGrid.displayName = "CardGrid";

// src/marketing/layouts/MarketingPage.tsx
import { ScrollView, View as View14, Platform as Platform7 } from "react-native";
import { jsx as jsx19 } from "react/jsx-runtime";
var MarketingPage = ({
  children,
  bg = marketing.canvas
}) => {
  const baseStyle = {
    flex: 1,
    backgroundColor: bg
  };
  if (Platform7.OS === "web") {
    return /* @__PURE__ */ jsx19(View14, { style: baseStyle, children });
  }
  return /* @__PURE__ */ jsx19(
    ScrollView,
    {
      style: baseStyle,
      contentContainerStyle: { flexGrow: 1 },
      children: /* @__PURE__ */ jsx19(View14, { style: { flex: 1 }, children })
    }
  );
};
MarketingPage.displayName = "MarketingPage";

// src/hooks/useBreakpoint.ts
import { useState as useState2, useEffect as useEffect2 } from "react";
import { Dimensions } from "react-native";
function getBreakpoint(width) {
  if (width < marketingBreakpoints.sm) return "sm";
  if (width < marketingBreakpoints.md) return "md";
  if (width < marketingBreakpoints.lg) return "lg";
  return "xl";
}
function useBreakpoint() {
  const [bp, setBp] = useState2(
    () => getBreakpoint(Dimensions.get("window").width)
  );
  useEffect2(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setBp(getBreakpoint(window.width));
    });
    return () => subscription.remove();
  }, []);
  return bp;
}
function useIsAbove(minBp) {
  const bp = useBreakpoint();
  const order = ["sm", "md", "lg", "xl"];
  return order.indexOf(bp) >= order.indexOf(minBp);
}

// src/marketing/examples/HomePage.tsx
import { View as View15 } from "react-native";
import {
  Lightning,
  Code,
  Compass,
  Stack,
  Sparkle,
  ArrowRight,
  Package,
  Lightbulb,
  CurrencyCircleDollar,
  Buildings,
  Check,
  Planet,
  XLogo,
  DiscordLogo,
  TelegramLogo
} from "@phosphor-icons/react";
import { jsx as jsx20, jsxs as jsxs11 } from "react/jsx-runtime";
var HomePage = () => /* @__PURE__ */ jsxs11(MarketingPage, { children: [
  /* @__PURE__ */ jsx20(
    Navbar,
    {
      logo: /* @__PURE__ */ jsxs11(View15, { style: { flexDirection: "row", alignItems: "center", gap: 8 }, children: [
        /* @__PURE__ */ jsx20(Icon, { icon: Planet, color: brand.purple, size: 24, weight: "duotone" }),
        /* @__PURE__ */ jsx20(Text, { size: 20, weight: "700", color: brand.white, fontFamily: "Plus Jakarta Sans", children: "LunarCrush" })
      ] }),
      links: [
        { label: "Product" },
        { label: "Use Cases" },
        { label: "Pricing" },
        { label: "Enterprise" }
      ],
      loginLink: { label: "Log in" },
      cta: { label: "Sign Up" }
    }
  ),
  /* @__PURE__ */ jsx20(
    Hero,
    {
      badge: {
        label: "Now available \xB7 LunarCrush for Claude",
        icon: /* @__PURE__ */ jsx20(Icon, { icon: Sparkle, color: brand.purple, size: 14, weight: "fill" })
      },
      headlineSans: "Real-Time Social & Market",
      headlineSerif: "Sentiment Powered by AI",
      description: "Build smarter with the world\u2019s largest crypto social dataset.\nIntegrate real-time sentiment, trends, and market signals\ninto your AI workflows and applications.",
      primaryCta: {
        label: "Get Started Free",
        icon: /* @__PURE__ */ jsx20(Icon, { icon: ArrowRight, color: "#FFFFFF", size: 16, weight: "bold" })
      },
      secondaryCta: { label: "View Products" },
      footer: /* @__PURE__ */ jsx20(TrustedBy, { names: ["ANTHROPIC", "CURSOR", "WINDSURF", "COINBASE"] })
    }
  ),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsxs11(Section, { children: [
    /* @__PURE__ */ jsx20(
      SectionBadge,
      {
        label: "Products",
        icon: /* @__PURE__ */ jsx20(Icon, { icon: Package, color: brand.purple, size: 14 })
      }
    ),
    /* @__PURE__ */ jsx20(
      Text,
      {
        size: 48,
        weight: "500",
        color: brand.white,
        fontFamily: "Butler",
        letterSpacing: -0.5,
        lineHeight: 48 * 1.2,
        align: "center",
        children: "Build with LunarCrush"
      }
    ),
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 17,
        color: marketing.textMuted,
        fontFamily: "Plus Jakarta Sans",
        lineHeight: 17 * 1.6,
        align: "center",
        style: { maxWidth: 576 },
        children: [
          "From AI-native integrations to raw API access, choose how",
          "\n",
          "you want to plug into real-time social media sentiment."
        ]
      }
    ),
    /* @__PURE__ */ jsxs11(CardGrid, { columns: 3, children: [
      /* @__PURE__ */ jsx20(
        FeatureCard,
        {
          title: "LunarCrush Claude Connector",
          description: "Connect Claude directly to real-time social media sentiment. Ask questions in natural language and get instant market insights.",
          icon: /* @__PURE__ */ jsx20(Icon, { icon: Lightning, color: brand.purple, size: 24 }),
          accentColor: brand.purple,
          iconTint: marketing.purpleTint,
          onPreviewPress: () => {
          },
          onLinkPress: () => {
          }
        }
      ),
      /* @__PURE__ */ jsx20(
        FeatureCard,
        {
          title: "LunarCrush for Claude Code",
          description: "Bring social data directly into your coding workflow. Build with real-time crypto sentiment right from your editor.",
          icon: /* @__PURE__ */ jsx20(Icon, { icon: Code, color: brand.cyan, size: 24 }),
          accentColor: brand.cyan,
          iconTint: marketing.cyanTint,
          onPreviewPress: () => {
          },
          onLinkPress: () => {
          }
        }
      ),
      /* @__PURE__ */ jsx20(
        FeatureCard,
        {
          title: "API",
          description: "Programmatic access to social metrics, sentiment scores, and market data for 4,000+ coins and 50K+ NFT collections.",
          icon: /* @__PURE__ */ jsx20(Icon, { icon: Code, color: brand.green, size: 24 }),
          accentColor: brand.green,
          iconTint: marketing.greenTint,
          onPreviewPress: () => {
          },
          onLinkPress: () => {
          }
        }
      ),
      /* @__PURE__ */ jsx20(
        FeatureCard,
        {
          title: "Discover",
          description: "Explore trending topics, emerging narratives, and sentiment shifts across the entire crypto social landscape.",
          icon: /* @__PURE__ */ jsx20(Icon, { icon: Compass, color: brand.amber, size: 24 }),
          accentColor: brand.amber,
          iconTint: marketing.amberTint,
          onPreviewPress: () => {
          },
          onLinkPress: () => {
          }
        }
      ),
      /* @__PURE__ */ jsx20(
        FeatureCard,
        {
          title: "Custom Collections",
          description: "Build tailored data streams across any combination of assets and topics. Your data, organized your way.",
          icon: /* @__PURE__ */ jsx20(Icon, { icon: Stack, color: brand.purpleLight, size: 24 }),
          accentColor: brand.purpleLight,
          iconTint: marketing.purpleTint,
          onPreviewPress: () => {
          },
          onLinkPress: () => {
          }
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsxs11(Section, { children: [
    /* @__PURE__ */ jsx20(
      SectionBadge,
      {
        label: "Use Cases",
        color: brand.cyan,
        tint: marketing.cyanTint,
        icon: /* @__PURE__ */ jsx20(Icon, { icon: Lightbulb, color: brand.cyan, size: 14 })
      }
    ),
    /* @__PURE__ */ jsx20(
      Text,
      {
        size: 48,
        weight: "500",
        color: brand.white,
        fontFamily: "Butler",
        letterSpacing: -0.5,
        lineHeight: 48 * 1.2,
        align: "center",
        children: "Built for every workflow"
      }
    ),
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 17,
        color: marketing.textMuted,
        fontFamily: "Plus Jakarta Sans",
        lineHeight: 17 * 1.6,
        align: "center",
        style: { maxWidth: 576 },
        children: [
          "Whether you're trading, building, or managing a fund,",
          "\n",
          "LunarCrush gives you the social edge."
        ]
      }
    ),
    /* @__PURE__ */ jsxs11(View15, { style: { flexDirection: "row", gap: sectionSpacing.cardGap, width: "100%" }, children: [
      /* @__PURE__ */ jsxs11(Card, { gap: 24, style: { flex: 1 }, children: [
        /* @__PURE__ */ jsx20(Text, { size: 20, weight: "600", color: brand.white, fontFamily: "Plus Jakarta Sans", children: "Investors & Traders" }),
        /* @__PURE__ */ jsx20(
          Text,
          {
            size: 14,
            color: marketing.textMuted,
            fontFamily: "Plus Jakarta Sans",
            lineHeight: 14 * 1.6,
            children: "Use social sentiment to inform trading decisions. Track Galaxy Score, AltRank, and real-time market signals to stay ahead of price moves."
          }
        ),
        /* @__PURE__ */ jsxs11(View15, { style: { gap: 12 }, children: [
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Galaxy Score\u2122 & AltRank\u2122", color: brand.purple, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.purple, size: 16, weight: "bold" }) }),
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Social sentiment signals", color: brand.purple, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.purple, size: 16, weight: "bold" }) }),
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Real-time trending alerts", color: brand.purple, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.purple, size: 16, weight: "bold" }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs11(Card, { gap: 24, style: { flex: 1 }, children: [
        /* @__PURE__ */ jsx20(Text, { size: 20, weight: "600", color: brand.white, fontFamily: "Plus Jakarta Sans", children: "Developers & Builders" }),
        /* @__PURE__ */ jsx20(
          Text,
          {
            size: 14,
            color: marketing.textMuted,
            fontFamily: "Plus Jakarta Sans",
            lineHeight: 14 * 1.6,
            children: "Build applications powered by social media sentiment. Use the API and MCP integrations to add LunarCrush data to any product or workflow."
          }
        ),
        /* @__PURE__ */ jsxs11(View15, { style: { gap: 12 }, children: [
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "REST API & MCP server", color: brand.cyan, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.cyan, size: 16, weight: "bold" }) }),
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Claude & AI agent ready", color: brand.cyan, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.cyan, size: 16, weight: "bold" }) }),
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Comprehensive documentation", color: brand.cyan, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.cyan, size: 16, weight: "bold" }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs11(Card, { gap: 24, style: { flex: 1 }, children: [
        /* @__PURE__ */ jsx20(Text, { size: 20, weight: "600", color: brand.white, fontFamily: "Plus Jakarta Sans", children: "Enterprise & Funds" }),
        /* @__PURE__ */ jsx20(
          Text,
          {
            size: 14,
            color: marketing.textMuted,
            fontFamily: "Plus Jakarta Sans",
            lineHeight: 14 * 1.6,
            children: "Hedge funds, exchanges, and research teams rely on LunarCrush for institutional-grade social media sentiment at scale."
          }
        ),
        /* @__PURE__ */ jsxs11(View15, { style: { gap: 12 }, children: [
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Dedicated account management", color: brand.amber, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.amber, size: 16, weight: "bold" }) }),
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "Custom data pipelines", color: brand.amber, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.amber, size: 16, weight: "bold" }) }),
          /* @__PURE__ */ jsx20(ChecklistItem, { label: "SLA & priority support", color: brand.amber, icon: /* @__PURE__ */ jsx20(Icon, { icon: Check, color: brand.amber, size: 16, weight: "bold" }) })
        ] })
      ] })
    ] })
  ] }),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsxs11(Section, { children: [
    /* @__PURE__ */ jsx20(
      Text,
      {
        size: 48,
        weight: "500",
        color: brand.white,
        fontFamily: "Butler",
        letterSpacing: -0.5,
        lineHeight: 48 * 1.2,
        align: "center",
        children: "The data behind the sentiment"
      }
    ),
    /* @__PURE__ */ jsxs11(View15, { style: { flexDirection: "row", gap: 24, width: "100%" }, children: [
      /* @__PURE__ */ jsx20(MetricCard, { value: "4,000+", label: "Coins Tracked", color: brand.purple }),
      /* @__PURE__ */ jsx20(MetricCard, { value: "2B+", label: "Social Posts Analyzed", color: brand.cyan }),
      /* @__PURE__ */ jsx20(MetricCard, { value: "50K+", label: "NFT Collections", color: brand.green }),
      /* @__PURE__ */ jsx20(MetricCard, { value: "Real-Time", label: "Data Updates", color: brand.amber })
    ] })
  ] }),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsxs11(Section, { children: [
    /* @__PURE__ */ jsx20(
      SectionBadge,
      {
        label: "Pricing",
        color: brand.green,
        tint: marketing.greenTint,
        icon: /* @__PURE__ */ jsx20(Icon, { icon: CurrencyCircleDollar, color: brand.green, size: 14 })
      }
    ),
    /* @__PURE__ */ jsx20(
      Text,
      {
        size: 48,
        weight: "500",
        color: brand.white,
        fontFamily: "Butler",
        letterSpacing: -0.5,
        lineHeight: 48 * 1.2,
        align: "center",
        children: "Simple, transparent pricing"
      }
    ),
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 17,
        color: marketing.textMuted,
        fontFamily: "Plus Jakarta Sans",
        lineHeight: 17 * 1.6,
        align: "center",
        style: { maxWidth: 576 },
        children: [
          "Start free, then scale as you grow. Every plan includes",
          "\n",
          "access to real-time social media sentiment."
        ]
      }
    ),
    /* @__PURE__ */ jsxs11(View15, { style: { flexDirection: "row", gap: sectionSpacing.cardGap, width: "100%" }, children: [
      /* @__PURE__ */ jsx20(
        PricingCard,
        {
          name: "Individual",
          description: "Starter access to the LunarCrush app, limited API calls and MCP server integration into LLMs.",
          price: "$72",
          ctaLabel: "CHOOSE INDIVIDUAL",
          accentColor: brand.green,
          features: [
            { title: "LunarCrush App", description: "Individual user access + a friend" },
            { title: "Agents", description: "Prompting + 1 agent, 1 million tokens/day" },
            { title: "Custom Terms", description: "1 collection, standard processing" },
            { title: "API", description: "Limited endpoints, 10 requests/min, 2,000/day" },
            { title: "MCP Server", description: "All tools within API limits" },
            { title: "Support", description: "Support portal" }
          ]
        }
      ),
      /* @__PURE__ */ jsx20(
        PricingCard,
        {
          name: "Builder",
          description: "Enhanced app access for small teams, enhanced API endpoints, powerful MCP integrations and support from real humans.",
          price: "$240",
          ctaLabel: "CHOOSE BUILDER",
          accentColor: brand.purple,
          features: [
            { title: "LunarCrush App", description: "Shared team access, up to 5 seats" },
            { title: "Agents", description: "Prompting + max 10 agents, 10M tokens/day" },
            { title: "Custom Terms", description: "Up to 20 collections, priority processing" },
            { title: "API", description: "All endpoints, 100 requests/min, 20,000/day" },
            { title: "MCP Server", description: "All tools within API limits" },
            { title: "Support", description: "Priority human support" }
          ]
        }
      ),
      /* @__PURE__ */ jsx20(
        PricingCard,
        {
          name: "Scale",
          description: "Broader app access for larger teams, increased API access, MCP integrations and priority support from real humans.",
          price: "$720",
          ctaLabel: "CHOOSE SCALE",
          accentColor: brand.green,
          features: [
            { title: "LunarCrush App", description: "Shared team access, up to 10 seats" },
            { title: "Agents", description: "Up to 50 agents, 30M tokens/day" },
            { title: "Custom Terms", description: "Up to 100 collections, priority processing" },
            { title: "API", description: "All endpoints, 500 requests/min, 100,000/day" },
            { title: "MCP Server", description: "All tools within API limits" },
            { title: "Support", description: "Premium human support" }
          ]
        }
      ),
      /* @__PURE__ */ jsx20(
        PricingCard,
        {
          name: "Enterprise",
          description: "Customized plans for teams with premium API access, support and more.",
          priceLabel: "Contact Sales",
          ctaLabel: "GET PRICING",
          accentColor: brand.purple,
          features: [
            { title: "LunarCrush App", description: "Organization access" },
            { title: "Agents", description: "Custom" },
            { title: "Custom Terms", description: "Custom" },
            { title: "API", description: "Custom" },
            { title: "MCP Server", description: "Custom" },
            { title: "Support", description: "Custom" }
          ]
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsxs11(Section, { children: [
    /* @__PURE__ */ jsx20(
      SectionBadge,
      {
        label: "Enterprise",
        color: brand.amber,
        tint: marketing.amberTint,
        icon: /* @__PURE__ */ jsx20(Icon, { icon: Buildings, color: brand.amber, size: 14 })
      }
    ),
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 48,
        weight: "500",
        color: brand.white,
        fontFamily: "Butler",
        letterSpacing: -0.5,
        lineHeight: 48 * 1.2,
        align: "center",
        style: { maxWidth: 768 },
        children: [
          "Enterprise-grade social media",
          "\n",
          "sentiment at scale"
        ]
      }
    ),
    /* @__PURE__ */ jsx20(
      Text,
      {
        size: 17,
        color: marketing.textMuted,
        fontFamily: "Plus Jakarta Sans",
        lineHeight: 17 * 1.6,
        align: "center",
        style: { maxWidth: 640 },
        children: "The world's leading hedge funds, exchanges, and research teams trust LunarCrush for institutional-quality crypto social data."
      }
    ),
    /* @__PURE__ */ jsx20(Button, { label: "Talk to Sales", variant: "primary" })
  ] }),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsxs11(Section, { paddingY: 112, children: [
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 56,
        weight: "500",
        color: brand.white,
        fontFamily: "Butler",
        letterSpacing: -0.5,
        lineHeight: 56 * 1.15,
        align: "center",
        style: { maxWidth: 640 },
        children: [
          "Ready to build with",
          "\n",
          "LunarCrush?"
        ]
      }
    ),
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 18,
        color: marketing.textMuted,
        fontFamily: "Plus Jakarta Sans",
        lineHeight: 18 * 1.6,
        align: "center",
        style: { maxWidth: 448 },
        children: [
          "Start integrating real-time social media sentiment",
          "\n",
          "into your workflows today. Free tier available."
        ]
      }
    ),
    /* @__PURE__ */ jsxs11(View15, { style: { flexDirection: "row", alignItems: "center", gap: 16 }, children: [
      /* @__PURE__ */ jsx20(Button, { label: "Get Started Free", variant: "primary" }),
      /* @__PURE__ */ jsx20(Button, { label: "Talk to Sales", variant: "outline" })
    ] }),
    /* @__PURE__ */ jsxs11(
      Text,
      {
        size: 13,
        color: marketing.textSubtle,
        fontFamily: "Plus Jakarta Sans",
        children: [
          "No credit card required ",
          "\xB7",
          " Free tier for developers ",
          "\xB7",
          " Enterprise plans available"
        ]
      }
    )
  ] }),
  /* @__PURE__ */ jsx20(Divider, {}),
  /* @__PURE__ */ jsx20(
    Footer,
    {
      logo: /* @__PURE__ */ jsxs11(View15, { style: { flexDirection: "row", alignItems: "center", gap: 8 }, children: [
        /* @__PURE__ */ jsx20(Icon, { icon: Planet, color: brand.purple, size: 20, weight: "duotone" }),
        /* @__PURE__ */ jsx20(Text, { size: 18, weight: "700", color: brand.white, fontFamily: "Plus Jakarta Sans", children: "LunarCrush" })
      ] }),
      tagline: "Real-time social media sentiment for crypto and web3.",
      columns: [
        {
          title: "Product",
          links: [
            { label: "Claude Connector" },
            { label: "For Claude Code" },
            { label: "API" },
            { label: "Discover" },
            { label: "Custom Collections" }
          ]
        },
        {
          title: "Use Cases",
          links: [
            { label: "Investors & Traders" },
            { label: "Developers & Builders" },
            { label: "Enterprise & Funds" }
          ]
        },
        {
          title: "Developers",
          links: [
            { label: "Documentation" },
            { label: "API Reference" },
            { label: "GitHub" }
          ]
        },
        {
          title: "Company",
          links: [
            { label: "About" },
            { label: "Blog" },
            { label: "Careers" }
          ]
        }
      ],
      copyright: "\\u00a9 2026 LunarCrush. All rights reserved.",
      socialLinks: [
        { label: "Twitter", icon: /* @__PURE__ */ jsx20(Icon, { icon: XLogo, color: marketing.textMuted, size: 18 }) },
        { label: "Discord", icon: /* @__PURE__ */ jsx20(Icon, { icon: DiscordLogo, color: marketing.textMuted, size: 18 }) },
        { label: "Telegram", icon: /* @__PURE__ */ jsx20(Icon, { icon: TelegramLogo, color: marketing.textMuted, size: 18 }) }
      ]
    }
  )
] });
HomePage.displayName = "HomePage";

export {
  Text,
  Pressable2 as Pressable,
  Divider,
  MarketingThemeProvider,
  useMarketingTheme,
  Container,
  Section,
  Button,
  Card,
  SectionBadge,
  GradientText,
  ChecklistItem,
  Navbar,
  Hero,
  Footer,
  DotGrid,
  FeatureCard,
  MetricCard,
  PricingCard,
  TrustedBy,
  CardGrid,
  MarketingPage,
  useBreakpoint,
  useIsAbove,
  HomePage
};
//# sourceMappingURL=chunk-7VBGLKOT.mjs.map